// Browser-local CRM. These records never use the portal's API or task store.
import { CONTACT_LIFECYCLES, normalizeCRMContacts, normalizeContact, validateContact } from './crmContacts';
import { activityBelongsTo } from '../shared/crm/activityLinks';
export const STAGES = [
    { id: 'qualified', label: 'Qualified', probability: 20, tone: 'blue' },
    { id: 'proposal', label: 'Proposal sent', probability: 50, tone: 'purple' },
    { id: 'negotiation', label: 'Negotiation', probability: 75, tone: 'amber' },
    { id: 'won', label: 'Closed won', probability: 100, tone: 'green' },
    { id: 'lost', label: 'Closed lost', probability: 0, tone: 'muted' },
];
export const LIFECYCLES = CONTACT_LIFECYCLES;
export const COMPANY_TYPES = ['Prospect', 'Client', 'Customer', 'Partner', 'Vendor'];
export const MESSAGE_TYPES = ['Email', 'SMS', 'WhatsApp message', 'LinkedIn message', 'Postal mail'];
export const ACTIVITY_TYPES = ['Note', 'Call', 'Email', 'Meeting', 'Task', 'SMS', 'WhatsApp message', 'LinkedIn message', 'Postal mail'];
export const isMessageType = kind => MESSAGE_TYPES.includes(kind);
export const activityTime = activity => new Date(activity.kind === 'Task' ? activity.completedAt || activity.createdAt : activity.date).getTime();
export function suggestedParticipant(data, kind, recordType, recordId) {
    const record = data[recordType]?.find(item => item.id === recordId);
    const contact = recordType === 'contacts' ? record : recordType === 'deals' ? data.contacts.find(item => item.id === record?.contactId) : null;
    if (contact) return (kind === 'Email' ? contact.email : ['SMS', 'WhatsApp message'].includes(kind) ? contact.phone : contact.name) || contact.name;
    return recordType === 'companies' ? record?.name || '' : '';
}
export const newId = () => window.crypto?.randomUUID?.() || `crm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const money = value => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value) || 0);
export const shortMoney = value => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', notation: 'compact', maximumFractionDigits: 1 }).format(Number(value) || 0);
export const day = value => value ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not set';
export const when = value => new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
export const initials = name => name.split(/\s+/).slice(0, 2).map(n => n[0]).join('').toUpperCase();
export const localDateTime = (value = new Date().toISOString()) => {
    const date = new Date(value);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
export const stageFor = id => STAGES.find(stage => stage.id === id) || STAGES[0];

export const createEmptyCRM = () => ({ version: 1, sampleCleanupVersion: 1, contacts: [], companies: [], deals: [], activities: [] });

// Retire the fixed IDs used by the original demo without resetting user-created data.
function removeLegacySamples(data) {
    if (data.sampleCleanupVersion >= 1) return data;
    const ids = (prefix, count) => new Set(Array.from({ length: count }, (_, index) => `${prefix}-${index + 1}`));
    const contacts = ids('ct', 7), companies = ids('co', 5), deals = ids('dl', 6), activities = ids('ac', 8);
    const removedByType = { contacts, companies, deals };
    const cleanContact = contact => ({ ...contact, ...(companies.has(contact.companyId) ? { companyId: '' } : {}) });
    const settings = data.contactSettings;
    return {
        ...data, sampleCleanupVersion: 1,
        contacts: data.contacts.filter(item => !contacts.has(item.id)).map(cleanContact),
        companies: data.companies.filter(item => !companies.has(item.id)),
        deals: data.deals.filter(item => !deals.has(item.id)).map(item => ({ ...item, ...(companies.has(item.companyId) ? { companyId: '' } : {}), ...(contacts.has(item.contactId) ? { contactId: '' } : {}) })),
        activities: data.activities.filter(item => !activities.has(item.id)).map(item => removedByType[item.recordType]?.has(item.recordId) ? { ...item, recordId: '' } : item),
        contactTrash: (data.contactTrash || []).filter(item => !contacts.has(item.id)).map(cleanContact),
        contactRelationships: (data.contactRelationships || []).filter(edge => !contacts.has(edge.fromId) && !contacts.has(edge.toId)),
        ...(settings ? { contactSettings: { ...settings,
            lists: (settings.lists || []).map(list => ({ ...list, contactIds: list.contactIds.filter(id => !contacts.has(id)) })),
            notifications: (settings.notifications || []).filter(event => !contacts.has(event.contactId)),
            ignoredDuplicates: (settings.ignoredDuplicates || []).filter(pair => !pair.split('|').some(id => contacts.has(id))),
        } } : {}),
    };
}

export const recordName = (data, type, id) => data[type]?.find(record => record.id === id)?.name || (type === 'contacts' && data.contactTrash?.some(contact => contact.id === id) ? 'Contact in recycle bin' : 'Unlinked record');
export function recordActivities(data, type, id) {
    return data.activities.filter(activity => activityBelongsTo(activity, type, id, data)).sort((a, b) => activityTime(b) - activityTime(a));
}

export function storageKey(user) {
    const identity = user?._id || user?.id || user?.email;
    return identity ? `gts.crm.prototype.v1.${encodeURIComponent(identity)}` : null;
}

export function loadCRM(key) {
    try {
        const raw = key && localStorage.getItem(key);
        if (!raw) return { data: normalizeCRMContacts(createEmptyCRM()), warning: key ? '' : 'Changes will last for this session only.' };
        const data = JSON.parse(raw);
        const groups = ['contacts', 'companies', 'deals', 'activities'];
        if (data.version !== 1 || !groups.every(group => Array.isArray(data[group]) && data[group].every(record => record && typeof record.id === 'string' && (group === 'activities' ? typeof record.subject === 'string' && ACTIVITY_TYPES.includes(record.kind) && ['contacts', 'companies', 'deals'].includes(record.recordType) && Number.isFinite(Date.parse(record.date)) && Number.isFinite(Date.parse(record.createdAt)) : typeof record.name === 'string')))) {
            return { data: normalizeCRMContacts(createEmptyCRM()), warning: 'Saved CRM data could not be opened. Changes will last for this session only.', preventSave: true };
        }
        return { data: normalizeCRMContacts(removeLegacySamples(data)), warning: '' };
    } catch {
        return { data: normalizeCRMContacts(createEmptyCRM()), warning: 'Browser storage is unavailable. Changes will last for this session only.', preventSave: true };
    }
}

export function validateRecord(type, record, data) {
    if (!record.name?.trim()) return 'Enter a name for this record.';
    if (type === 'contacts') return validateContact(normalizeContact(record), data);
    if (type === 'deals') {
        if (!Number.isFinite(Number(record.amount)) || Number(record.amount) < 0) return 'Enter a valid amount of zero or more.';
        if (!STAGES.some(stage => stage.id === record.stage)) return 'Choose a valid deal stage.';
        if (record.stage === 'lost' && !record.lostReason?.trim()) return 'Add a reason for closing this deal as lost.';
        const previous = data.deals.find(deal => deal.id === record.id);
        const unchangedAssociation = previous?.contactId === record.contactId && previous?.companyId === record.companyId;
        if (!unchangedAssociation && record.contactId && record.companyId && !data.contacts.some(contact => contact.id === record.contactId && contact.companyId === record.companyId)) return 'Choose a contact associated with the selected company.';
    }
    return '';
}
